/** Jarvis Knowledge — add a PDF (drag & drop), a web link or a typed note. */
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud } from 'lucide-react';
import api from '../../lib/api';
import { btnGhost, btnPrimary, errorText, inputCls } from '../finance/financeUi';
import { upload } from './PackList';

export function AddKnowledge({ packId, onAdded }) {
  const [mode, setMode] = useState(null);
  const [f, setF] = useState({ url: '', title: '', text: '' });
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const done = (msg) => { toast.success(msg); setMode(null); setF({ url: '', title: '', text: '' }); onAdded(); };
  const sendPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files can be uploaded'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('PDFs are limited to 25 MB'); return; }
    try { await upload(`/jarvis/packs/${packId}/sources/pdf`, file, { title: file.name.replace(/\.pdf$/i, '') }); done('PDF uploaded — processing'); } catch (e) { toast.error(e.message); }
  };
  const post = async (path, body, msg) => {
    try { await api.request(path, { method: 'POST', body: JSON.stringify(body) }); done(msg); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-3 px-5 py-4" data-testid="jarvis-add-knowledge">
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
           onDrop={(e) => { e.preventDefault(); setDrag(false); sendPdf(e.dataTransfer.files[0]); }}
           onClick={() => fileRef.current?.click()} data-testid="jarvis-pdf-dropzone"
           className={`flex cursor-pointer flex-col items-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-sm transition-colors ${drag ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'}`}>
        <UploadCloud className="h-6 w-6 text-muted-foreground" />
        <span>Drop a PDF here or click to choose one</span>
        <span className="text-xs text-muted-foreground">Text-based PDFs up to 25 MB. Scanned PDFs can't be read yet.</span>
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => sendPdf(e.target.files[0])} data-testid="jarvis-pdf-input" />
      </div>
      <div className="flex gap-2">
        <button type="button" className={btnGhost} onClick={() => setMode(mode === 'link' ? null : 'link')} data-testid="jarvis-add-link-btn">Add link</button>
        <button type="button" className={btnGhost} onClick={() => setMode(mode === 'note' ? null : 'note')} data-testid="jarvis-add-note-btn">Add note</button>
      </div>
      {mode === 'link' && (
        <div className="flex flex-wrap gap-2" data-testid="jarvis-link-form">
          <input className={`${inputCls} max-w-md`} placeholder="https://… (public page)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="jarvis-link-url" />
          <input className={`${inputCls} max-w-xs`} placeholder="Title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="jarvis-link-title" />
          <button type="button" className={btnPrimary} disabled={!f.url.trim()} onClick={() => post(`/jarvis/packs/${packId}/sources/link`, { url: f.url, title: f.title }, 'Link saved — capturing the page')} data-testid="jarvis-link-save-btn">Save link</button>
        </div>
      )}
      {mode === 'note' && (
        <div className="space-y-2" data-testid="jarvis-note-form">
          <input className={`${inputCls} max-w-md`} placeholder="Title, e.g. Dual Pricing pitch script" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="jarvis-note-title" />
          <textarea className={`${inputCls} min-h-[140px]`} placeholder="Type or paste the script, answer or procedure. Use # headings for sections." value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="jarvis-note-text" />
          <button type="button" className={btnPrimary} disabled={!f.title.trim() || !f.text.trim()} onClick={() => post(`/jarvis/packs/${packId}/sources/note`, { title: f.title, text: f.text }, 'Note saved — processing')} data-testid="jarvis-note-save-btn">Save note</button>
        </div>
      )}
    </div>
  );
}
